import { useQuery } from "@tanstack/react-query";
import {
  PublicAssetFrontendApi,
  PublicAssetFrontendApi$AssetComponentDto,
  PublicAssetFrontendApi$AssetComponentDtoKey,
  PublicAssetFrontendApi$AssetComponents,
  PublicAssetFrontendApi$ViewDetails,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import useUiContext from "@/custolink-hooks/useUiContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAssetViewTranslator from "@/custolink-views/asset_frontend/useAssetViewTranslator";
import { AxiosError } from "axios";

export type AssetViewFunction = "Account" | "CreateAccount" | "Scan";

export type AssetViewUrlParams = {
  viewId: number;
  viewValue: string;
  assetId: number;
  keyString: string;
};

export type AssetViewDetails = PublicAssetFrontendApi$ViewDetails & {
  urlParamsForRequests: AssetViewUrlParams;
};

export type useAssetViewFacadeRt = {
  isRequestPending: boolean;
  assetViewOrAssetNotFound: boolean;
  /**
   * The asset is hidden from this visitor (Asset Protection, "Hide whole asset").
   * The server sent no serial number, product name or components, so `details`
   * is null; the page shows the sign-in-required screen instead.
   */
  protectedSignInRequired: boolean;
  details: AssetViewDetails | null;

  hideViewFunctionOrDrawer: () => void;
  selectedComponent: PublicAssetFrontendApi$AssetComponentDto | null;
  selectComponent: (cmp: PublicAssetFrontendApi$AssetComponentDto) => void;
  assetViewFunction: AssetViewFunction | null;
  showAccountView: () => void;
  showScanView: () => void;
};

export default function useAssetViewFacade(
  serialNumber: string,
  urlSlug: string,
  assetAttrValue: string,
): useAssetViewFacadeRt {
  const uic = useUiContext();
  const t = useAssetViewTranslator();

  const q = useQuery({
    retryDelay: 20,
    queryFn: () =>
      PublicAssetFrontendApi.getViewDetails(
        uic._orgId,
        serialNumber,
        urlSlug,
        assetAttrValue,
      ),
    queryKey: [
      "PublicAssetFrontendApi",
      PublicAssetFrontendApi.getViewDetails.name,
      uic._orgId,
      serialNumber,
      urlSlug,
      assetAttrValue,
      // Asset Protection decides per visitor: signing in or out must ask the
      // server again instead of reusing what the previous identity was shown.
      uic.assetFrontendAuthedUser?.username ?? "",
    ],
  });

  const protectedSignInRequired = q.data?.protectedSignInRequired === true;

  const [assetViewOrAssetNotFound, setAssetViewOrAssetNotFound] =
    useState(false);

  const [assetViewFunction, setAssetViewFunction] =
    useState<AssetViewFunction | null>(null);

  const [selectedComponent, setSelectedComponent] =
    useState<PublicAssetFrontendApi$AssetComponentDto | null>(null);

  const info = useMemo(() => {
    if (q.data == null) {
      return null;
    }
    // A hidden asset comes back without any inferred view; there is nothing to
    // request files, search or calls for.
    if (q.data.protectedSignInRequired) {
      return null;
    }
    const cc: PublicAssetFrontendApi$AssetComponents = {
      bottomSection: t.translateNames(q.data.components.bottomSection),
      topSection: t.translateNames(q.data.components.topSection),
      middleSection: t.translateNames(q.data.components.middleSection),
    };
    let urlParamsForRequests: AssetViewUrlParams;
    if (q.data.inferredDefaultView) {
      urlParamsForRequests = {
        viewId: q.data.id,
        viewValue: "",
        assetId: q.data.assetId,
        keyString: "",
      };
    } else if (q.data.inferredCategoryView) {
      urlParamsForRequests = {
        viewId: q.data.id,
        viewValue: q.data.inferredCategoryView.viewValue,
        assetId: q.data.assetId,
        keyString: "",
      };
    } else if (q.data.inferredSerialnumberPatternMatchView) {
      urlParamsForRequests = {
        viewId: q.data.id,
        viewValue: q.data.inferredSerialnumberPatternMatchView.serialNumber,
        assetId: q.data.assetId,
        keyString: "",
      };
    } else {
      throw Error("illegal state");
    }
    urlParamsForRequests.keyString = urlParamsForRequests.viewId+"_"+urlParamsForRequests.viewValue+"_"+urlParamsForRequests.assetId;
    return {
      ...q.data,
      components: cc,
      urlParamsForRequests: urlParamsForRequests,
    };
  }, [q.data]);

  const handlePopState = useCallback(() => {
    if (assetViewFunction != null || selectedComponent != null) {
      setAssetViewFunction(null);
      setSelectedComponent(null);
    }
  }, [assetViewFunction, selectedComponent]);

  useEffect(() => {
    if (selectedComponent != null || assetViewFunction != null) {
      if (window.history.state?._componentShown != true) {
        window.history.pushState({ _componentShown: true }, "");
      }
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectedComponent, assetViewFunction, handlePopState]);

  // Once the visitor signs in with an approved account the asset becomes
  // visible; close the Account drawer so it does not cover the page.
  const [wasProtected, setWasProtected] = useState(protectedSignInRequired);
  if (wasProtected !== protectedSignInRequired && q.data != null) {
    setWasProtected(protectedSignInRequired);
    if (wasProtected && !protectedSignInRequired) {
      setAssetViewFunction(null);
    }
  }

  useEffect(() => {
    if (q.error != null) {
      if ((q.error as AxiosError)?.status == 422) {
        setAssetViewOrAssetNotFound(true);
      }
    }
  }, [q.error]);

  return {
    isRequestPending: q.isPending,
    assetViewOrAssetNotFound: assetViewOrAssetNotFound,
    protectedSignInRequired: protectedSignInRequired,
    details: info,

    hideViewFunctionOrDrawer: () => {
      setSelectedComponent(null);
      setAssetViewFunction(null);
      if (window.history.state?._componentShown == true) {
        window.history.back();
      }
      // D-04: drawer X only hides UI, does not cancel call
    },

    selectedComponent: selectedComponent,
    selectComponent: (cmp: PublicAssetFrontendApi$AssetComponentDto) => {
      setAssetViewFunction(null);
      setSelectedComponent(null);

      if (
        cmp.key == PublicAssetFrontendApi$AssetComponentDtoKey.VideoCall ||
        cmp.key == PublicAssetFrontendApi$AssetComponentDtoKey.AiSupport ||
        cmp.key ==
          PublicAssetFrontendApi$AssetComponentDtoKey.RegisterProduct ||
        cmp.key == PublicAssetFrontendApi$AssetComponentDtoKey.Inquiry
      ) {
        if (uic.assetFrontendAuthedUser != null) {
          setSelectedComponent(cmp);
        } else {
          setAssetViewFunction("CreateAccount");
        }
      } else {
        setSelectedComponent(cmp);
      }
    },

    assetViewFunction: assetViewFunction,
    showAccountView: () => {
      // The component drawer mounts after the Account drawer and would cover
      // it, so a sign-in asked for from inside a component closes that first.
      setSelectedComponent(null);
      setAssetViewFunction("Account");
    },
    showScanView: () => {
      setAssetViewFunction("Scan")
    }
  };
}
